<template>
  <b-modal
    v-model="localIsActive"
    centered
    no-close-on-esc
    no-close-on-backdrop
    hide-footer
    dialog-class="cameras-list-dialog"
    header-bg-variant="primary"
    @show="initData()"
    @hidden="closeModal()"
  >
    <template #modal-title>
      <span class="text-white">Seleccionar cámaras</span>
    </template>
    <div class="row">
      <div class="col-6">
        <b-form-group
          :label="$tc('unit.title', 1)"
          label-for="units"
        >
          <v-select
            label="description"
            v-model="unit"
            class="w-100"
            :options="facilityOptions"
            :filterable="false"
            :reduce="(val) => val.id"
            :disabled="loadingCameras"
            @search="onSearchFacility"
            @input="onChangeFacilitySelect"
          />
        </b-form-group>
      </div>
      <div class="col-6">
        <b-form-group
          label="Buscar cámaras"
          label-for="cameras"
        >
          <b-form-input
            v-model="camerasQuery"
            class="d-inline-block mr-1"
            :placeholder="$t('table.search') + '...'"
            :disabled="disabledInputSearch"
            @input="onSearchCameras"
          />
        </b-form-group>
      </div>
    </div>
    <b-table
      class="camera-table"
      :busy="loadingCameras"
      :items="camerasOptions"
      :fields="fields"
      sticky-header="350px"
      show-empty
    >
      <template #table-busy>
        <div class="cameras-list d-flex justify-content-center align-items-center">
          <b-spinner variant="primary" />
        </div>
      </template>
      <template #empty>
        <div class="cameras-list d-flex justify-content-center align-items-center">
          Sin información de cámaras
        </div>
      </template>
      <template #cell(actions)="data">
        <b-form-checkbox
          v-model="data.item.selected"
          :name="`check-${data.item.value}`"
          switch
        />
      </template>
    </b-table>
    <div class="text-right">
      <b-button
        type="button"
        variant="primary"
        :disabled="disabledAcceptButton"
        @click="buildCamerasByUnit()"
      >
        <span class="text-nowrap">Aceptar</span>
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { computed,ref } from "@vue/composition-api";
import { debounce } from 'lodash';
import popperHelper from "@/libs/popper-helper";
import vSelect from "vue-select";
import axios from "@axios";
import store from '@/store';
import { cameraOptions } from "@/libs/timeline";

export default {
  components: {
    vSelect
  },
  props: {
    itemData: {
      type: Object,
      require: true
    },
    showCamerasListModal: {
      type: Boolean,
      require: true
    }
  },
  setup(props, { emit }) {
    //COMPOSABLES

    //DATA
    const camerasQuery = ref(null);

    const facilitySearch = ref(null);

    const facilityOptions = ref([]);

    const loadingCameras = ref(false);

    const fields = ref([
      {
        key: 'label',
        label: 'Nombre',
        thClass: 'text-center',
        tdClass: 'text-center'
      },
      {
        key: 'actions',
        label: 'Seleccionar',
        thClass: 'text-center',
        tdClass: 'text-center'
      }
    ]);

    const unit = ref(null);

    //COMPUTED
    const camerasOptions = computed(() => {
      const cameras = store.getters['cm_proxy/getSelectedItems'].map(camera => ({
        label: camera.label,
        value: camera.value,
        selected: camerasIsSelected(camera.value)
      }));

      return cameras;
    });

    const disabledAcceptButton = computed(() => {
      const camerasSelected = camerasOptions.value.filter(camera => camera.selected);

      return (!unit.value || loadingCameras.value || !camerasSelected.length)
    });

    const disabledInputSearch = computed(() => (!unit.value || loadingCameras.value));

    const localIsActive = computed({
      get() {
        return props.showCamerasListModal;
      },
      set(value) {
        emit("update:isActive", value);
      },
    });

    //METHODS
    const buildCamerasByUnit = () => {
      const camerasSelected = camerasOptions.value.filter(camera => camera.selected).map(camera => ({
        ...camera,
        from: camerasSelectedForTime(camera, 'from'),
        to: camerasSelectedForTime(camera, 'to')
      }));

      const selectedUnit    = facilityOptions.value.find(facility => (facility.id === unit.value));
      const registeredUnit  = props.itemData.cameras.find(camera => (camera.unit.id === selectedUnit.id));

      if (registeredUnit) {
        registeredUnit.cameras = camerasSelected
      } else {
        const dataSelected = {
          unit: null,
          cameras: []
        }

        dataSelected.cameras = camerasSelected;

        dataSelected.unit = {
          id: selectedUnit.id,
          description: selectedUnit.description
        }

        props.itemData.cameras.push(dataSelected);
      }

      closeModal();
    }

    const camerasSelectedForTime = (cameraToEvaluate, field) => {
      const unitCameras = props.itemData.cameras.find(camera => (camera.unit.id === unit.value));

      if (!unitCameras) {
        return null;
      }

      const unitCamera = unitCameras.cameras.find(camera => (camera.value === cameraToEvaluate.value));

      return (unitCamera) ? unitCamera[field] : null;
    }

    const camerasIsSelected = (cameraToEvaluate) => {
      const unitCameras = props.itemData.cameras.find(camera => (camera.unit.id === unit.value));

      if (!unitCameras) {
        return false;
      }

      const unitCamera = unitCameras.cameras.find(camera => (camera.value === cameraToEvaluate.value));

      return (unitCamera) ? true : false;
    }

    const closeModal = () => {
      facilitySearch.value = null;
      emit('update:isActive', false);
    }

    const fetchFacilityData = async () => {
      try {
        const { data } = await axios.get('/v1/unit/tenants', {
          params: {
            q: facilitySearch.value,
            max: 10
          }
        });

        facilityOptions.value = data.data.data;
      } catch (error) {
        facilityOptions.value = [];
        console.error(`FacilitySelect: request error`, error?.response || error);
      }
    }

    const initData = async () => {
      unit.value = null;

      store.commit('cm_proxy/setData', []);
      await fetchFacilityData();
    }

    const onChangeFacilitySelect = async (unit) => {
      loadingCameras.value = true;

      await store.dispatch('cm_proxy/getAll', {
        camerasOnly: true,
        units: [unit],
        relations: ['units']
      });

      loadingCameras.value = false;
    }

    const onSearchCameras = debounce(async function () {
      loadingCameras.value = true;

      await store.dispatch('cm_proxy/getAll', {
        q: camerasQuery.value,
        camerasOnly: true,
        units: [unit.value]
      });

      loadingCameras.value = false;
    }, 400);

    const onSearchFacility = debounce(async (query, loading) => {
      if (query && query.length) {
        facilitySearch.value = query;

        loading(true);

        await fetchFacilityData();

        loading(false);
      }
    }, 500);

    return {
      camerasQuery,
      loadingCameras,
      facilitySearch,
      facilityOptions,
      fields,
      unit,

      camerasOptions,
      disabledAcceptButton,
      disabledInputSearch,
      localIsActive,

      buildCamerasByUnit,
      closeModal,
      fetchFacilityData,
      initData,
      onChangeFacilitySelect,
      onSearchCameras,
      onSearchFacility
    }
  }
}
</script>

<style scoped>
::v-deep .cameras-list-dialog {
    max-width: 45vw;
}

.cameras-list {
  height: 200px !important;
  max-height: 200px !important;
}
::v-deep .camera-table.b-table-sticky-header {
  height: 300px !important;
  max-height: 300px !important;
}
</style>
